<div class="integration-section">
  <div class="integration-layout" [loadingOverlay]="isLoading">
    <div class="integration-main">
      <div class="d-flex align-items-center gap-2 mb-2">
        <h6 class="mb-0">
          Google Integration
          <app-components-helptext [key]="'GoogleCustomerAccounts_IntegrationStatus'"
            [noMargin]="true"></app-components-helptext>
        </h6>
        <span *ngIf="isGoogleIntegrationSetupCompleted" class="badge bg-success">
          <i class="fa fa-check-circle me-1"></i>Google Integration Completed
        </span>
        <span *ngIf="isGoogleIntegrationSetupInProgress" class="badge bg-info">
          <i class="fa fa-spinner fa-spin me-1"></i>Setup In Progress
        </span>
        <span *ngIf="isGoogleIntegrationSetupRedoInProgress" class="badge bg-info">
          <i class="fa fa-spinner fa-spin me-1"></i>Redo In Progress
        </span>
        <button *ngIf="isGoogleIntegrationSetupRedoInProgress && !isReadOnly" type="button"
          class="close-redo" (click)="closeGoogleIntegrationSetupRedo()"
          [disabled]="isClosingRedo">
          <i class="fa fa-xmark"></i>Close Redo
        </button>
        <span *ngIf="isGoogleIntegrationSetupMissingCredentials" class="badge bg-warning text-dark">
          <i class="fa fa-exclamation-triangle me-1"></i>CMS Credentials Required
        </span>
      </div>
      <p class="text-muted small mb-3" *ngIf="summarySubtitle">{{ summarySubtitle }}</p>

      <div *ngIf="isGoogleIntegrationSetupRequested" class="alert alert-warning mb-3" role="alert">
        <span class="me-2">Google Integration Setup</span>
        <span class="float-end">Requested</span>
      </div>

      <div class="service-row">
        <!-- Analytics -->
        <div>
          <div class="service-card" [class.connected]="hasGoogleAnalyticsAccess">
            <div class="service-icon" [class.icon-connected]="hasGoogleAnalyticsAccess"
              [class.icon-disconnected]="!hasGoogleAnalyticsAccess">
              <i class="fa fa-fw fa-chart-line"></i>
              <span class="status-indicator">
                <i class="fa" [ngClass]="hasGoogleAnalyticsAccess ? 'fa-check' : 'fa-xmark'"></i>
              </span>
            </div>
            <div class="service-name">Analytics</div>
            <button *ngIf="hasGoogleAnalyticsAccess && !isReadOnly" type="button" class="service-link text-danger"
              (click)="revokeGoogleAnalyticsAccess()" [disabled]="isRevokingGoogleAnalyticsAccess">
              Revoke
            </button>
          </div>
        </div>

        <!-- Search Console -->
        <div>
          <div class="service-card" [class.connected]="canIndexInGoogleSearchConsole">
            <div class="service-icon" [class.icon-connected]="canIndexInGoogleSearchConsole"
              [class.icon-disconnected]="!canIndexInGoogleSearchConsole">
              <i class="fa fa-fw fa-search"></i>
              <span class="status-indicator">
                <i class="fa" [ngClass]="canIndexInGoogleSearchConsole ? 'fa-check' : 'fa-xmark'"></i>
              </span>
            </div>
            <div class="service-name">Search Console</div>
          </div>
        </div>

        <!-- Business Profile -->
        <div>
          <div class="service-card" [class.connected]="hasGoogleBusinessProfileAccess">
            <div class="service-icon" [class.icon-connected]="hasGoogleBusinessProfileAccess"
              [class.icon-disconnected]="!hasGoogleBusinessProfileAccess">
              <i class="fa fa-fw fa-store"></i>
              <span class="status-indicator">
                <i class="fa" [ngClass]="hasGoogleBusinessProfileAccess ? 'fa-check' : 'fa-xmark'"></i>
              </span>
            </div>
            <div class="service-name">Business Profile</div>
            <button *ngIf="!hasGoogleBusinessProfileAccess" type="button" class="service-link text-muted"
              (click)="copyGoogleBusinessProfileConnectorLink()">
              <i class="fa fa-copy"></i> Copy GBP Link
            </button>
          </div>
        </div>

        <!-- Order setup action (first-time only) -->
        <div *ngIf="canOrderGoogleIntegrationSetup && !isReadOnly">
          <div class="service-card">
            <button type="button" class="service-icon icon-start" (click)="orderGoogleIntegrationSetup()"
              [disabled]="isOrderingIntegration" title="Order Google Integration">
              <i class="fa fa-fw fa-play"></i>
            </button>
            <div class="service-name">Order Google<br>Integration</div>
          </div>
        </div>

        <!-- Order task redo action (completed integrations only) -->
        <div *ngIf="canOrderGoogleIntegrationSetupRedo && !isOrderingIntegrationRedo && !isReadOnly">
          <div class="service-card">
            <button type="button" class="service-icon icon-start" (click)="orderGoogleIntegrationSetupRedo()"
              title="Order Task Redo">
              <i class="fa fa-fw fa-rotate"></i>
            </button>
            <div class="service-name">Order Task<br>Redo</div>
          </div>
        </div>
      </div>
    </div>
    <div class="integration-side d-flex flex-column gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary text-start"
        (click)="openAccountInformation()">
        <i class="fa fa-list-ul me-1" aria-hidden="true"></i>Account Info
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary text-start"
        (click)="openDiagnostic()">
        <i class="fa fa-clipboard-list me-1" aria-hidden="true"></i>Diagnostics
      </button>
    </div>
    <!-- Right column: site-presence panel, top-aligned with the header -->
    <div class="integration-side">
      <!-- Site-presence panel: per-snippet status + copy chip + shared footer -->
      <div class="presence-panel">
        <div class="presence-panel-row presence-panel-inline-row">
          <div class="presence-panel-status" [class.status-pass]="gtmTagStatus === GoogleIntegrationSiteStatuses.Pass"
            [class.status-fail]="gtmTagStatus === GoogleIntegrationSiteStatuses.Fail"
            [class.status-warning]="gtmTagStatus === GoogleIntegrationSiteStatuses.Unreachable"
            [class.status-info]="gtmTagStatus === GoogleIntegrationSiteStatuses.NotConfigured"
            [class.status-unknown]="isStatusUnknown(gtmTagStatus)">
            <i class="status-icon fa" aria-hidden="true"
              [class.fa-circle-check]="gtmTagStatus === GoogleIntegrationSiteStatuses.Pass"
              [class.fa-circle-xmark]="gtmTagStatus === GoogleIntegrationSiteStatuses.Fail"
              [class.fa-triangle-exclamation]="gtmTagStatus === GoogleIntegrationSiteStatuses.Unreachable"
              [class.fa-circle-info]="gtmTagStatus === GoogleIntegrationSiteStatuses.NotConfigured"
              [class.fa-circle-question]="isStatusUnknown(gtmTagStatus)"></i>
            <span class="status-label">{{ getGtmStatusSubject() }} {{ getSiteFoundText(gtmTagStatus) }}</span>
          </div>
          <button *ngIf="gtmId" type="button" class="btn btn-link btn-sm p-0 align-baseline presence-copy-chip"
            title="Copy GTM Tag" (click)="copyGtmId()">
            {{ gtmId }}<i class="fa fa-copy ms-1" aria-hidden="true"></i>
          </button>
          <span *ngIf="isGtmTagOutOfSync" class="presence-mismatch-inline"
            title="The current GTM id differs from what was verified on the site">
            <i class="fa fa-triangle-exclamation" aria-hidden="true"></i> verified {{ lastCheckedExpectedGtmId }}
          </span>
        </div>
        <div class="presence-panel-row presence-panel-inline-row">
          <div class="presence-panel-status"
            [class.status-pass]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Pass"
            [class.status-fail]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Fail"
            [class.status-warning]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Unreachable"
            [class.status-info]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.NotConfigured"
            [class.status-unknown]="isStatusUnknown(verificationSnippetStatus)">
            <i class="status-icon fa" aria-hidden="true"
              [class.fa-circle-check]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Pass"
              [class.fa-circle-xmark]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Fail"
              [class.fa-triangle-exclamation]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.Unreachable"
              [class.fa-circle-info]="verificationSnippetStatus === GoogleIntegrationSiteStatuses.NotConfigured"
              [class.fa-circle-question]="isStatusUnknown(verificationSnippetStatus)"></i>
            <span class="status-label">Verification Snippet {{ getSiteFoundText(verificationSnippetStatus) }}</span>
          </div>
          <button *ngIf="scVerificationHtmlTag" type="button"
            class="btn btn-link btn-sm p-0 align-baseline presence-copy-chip" title="Copy Verification Snippet"
            (click)="copyVerificationSnippet()">
            GSC Verification Snippet<i class="fa fa-copy ms-1" aria-hidden="true"></i>
          </button>
          <span *ngIf="isVerificationSnippetOutOfSync" class="presence-mismatch-inline"
            title="The current verification token differs from what was verified on the site">
            <i class="fa fa-triangle-exclamation" aria-hidden="true"></i> verified token differs
          </span>
        </div>
        <div class="presence-panel-row presence-panel-footer">
          <span class="presence-panel-meta">
            <ng-container *ngIf="sitePresenceLastCheckedDate">Last checked {{ sitePresenceLastCheckedDate |
              date:'shortDate' }}</ng-container>
            <ng-container *ngIf="!sitePresenceLastCheckedDate">Not yet checked</ng-container>
          </span>
          <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="isSitePresenceButtonDisabled"
            (click)="checkSiteForGoogleIntegrations()">
            <i class="fa me-1" aria-hidden="true" [class.fa-rotate]="!isSitePresenceButtonDisabled"
              [class.fa-spinner]="isSitePresenceButtonDisabled" [class.fa-spin]="isSitePresenceButtonDisabled"></i>{{
            getSitePresenceButtonText() }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #accountInfoDialogTemplate let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Account Information</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body" *ngIf="accountInformation">
    <div class="account-info-group mb-3">
      <div class="account-info-group-title">Tag Manager</div>
      <div class="account-info-row">
        <span class="account-info-label">Account</span>
        <span class="account-info-value">{{ accountInformation.GtmAccount || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">Partner/Bucket Account</span>
        <span class="account-info-value">{{ accountInformation.GtmBucketAccount || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">Container</span>
        <span class="account-info-value">{{ accountInformation.GtmContainerName || 'N/A' }}</span>
      </div>
    </div>
    <div class="account-info-group mb-3">
      <div class="account-info-group-title">Analytics</div>
      <div class="account-info-row">
        <span class="account-info-label">Account</span>
        <span class="account-info-value">{{ accountInformation.GaAccount || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">Partner/Bucket Account</span>
        <span class="account-info-value">{{ accountInformation.GaBucketAccount || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">Property</span>
        <span class="account-info-value">{{ accountInformation.GaPropertyName || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">Property ID</span>
        <span class="account-info-value">{{ accountInformation.GaPropertyId || 'N/A' }}</span>
      </div>
    </div>
    <div class="account-info-group">
      <div class="account-info-group-title">Search Console</div>
      <div class="account-info-row">
        <span class="account-info-label">Account</span>
        <span class="account-info-value">{{ accountInformation.SearchConsoleAccount || 'N/A' }}</span>
      </div>
      <div class="account-info-row">
        <span class="account-info-label">URL</span>
        <span class="account-info-value">{{ accountInformation.SearchConsoleUrl || 'N/A' }}</span>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Close</button>
  </div>
</ng-template>

<ng-template #cmsAdminDialogTemplate let-modal>
  <div class="modal-header">
    <h4 class="modal-title">{{ isRedoOrder ? 'Order Google Integration Redo' : 'Order Google Integration' }}</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="form-check">
      <label class="form-check-label">
        <input class="form-check-input" type="checkbox" [(ngModel)]="isCmsAdminCredentialsVerified">
        I have verified CMS Admin credentials are working.
      </label>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-primary"
      [disabled]="!isCmsAdminCredentialsVerified || (isRedoOrder ? isOrderingIntegrationRedo : isOrderingIntegration)"
      (click)="confirmGoogleIntegrationSetupOrder(modal)">
      Order
    </button>
  </div>
</ng-template>

<ng-template #closeRedoDialogTemplate let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Close Google Integration Redo</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p class="mb-0">Close the in-progress Google integration redo? This also closes any related workblock ticket.</p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-danger" [disabled]="isClosingRedo"
      (click)="confirmCloseGoogleIntegrationSetupRedo(modal)">
      Close Redo
    </button>
  </div>
</ng-template>

<ng-template #revokeAnalyticsDialogTemplate let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Revoke Analytics Access</h4>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="alert alert-warning mb-2" role="alert">
      Revoking access will disconnect this customer from Google Analytics data.
    </div>
    <div class="form-check">
      <label class="form-check-label">
        <input class="form-check-input" type="checkbox" [(ngModel)]="isRevokeAnalyticsConfirmed">
        I understand this will revoke Analytics access for this customer.
      </label>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-danger"
      [disabled]="!isRevokeAnalyticsConfirmed || isRevokingGoogleAnalyticsAccess"
      (click)="confirmRevokeGoogleAnalyticsAccess(modal)">
      Revoke Access
    </button>
  </div>
</ng-template>